import dayjs from 'dayjs';
import {loadScript} from './loadScript';
import {isAvailabiltyRoute} from './isRoute';
import {hasCookieConsent} from './hasCookieConsent';
import utc from 'dayjs/plugin/utc';

interface EventData {
  category: Category;
  property: string;
  action: Action;
  label?: string;
  value?: string;
}

type GtmTrackEvent = {
  event: 'trackEvent' | 'authenticationState';
  eventData: EventData;
  eventTimeout?: number;
};

export const GTM_ID = 'GTM-K5GQMC';
export const loadGTMLibrary = async () => {
  const hasConsent = await hasCookieConsent('targeting');
  if (!hasConsent) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  return loadScript(
    `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`,
  ).catch(() => {});
};

const transformDate = (date: string) => {
  return date.split('-').join('');
};

export const track = (payload: GtmTrackEvent, eventTimeout = 500) =>
  new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, eventTimeout);

    window.dataLayer.push({
      ...payload,
      eventDestination: 'GA4',
      eventTimeout,
      eventCallback: () => {
        window.clearTimeout(timeout);
        resolve();
      },
    });
  });

export const createEvent = ({
  category,
  property,
  action,
  label,
  value,
}: EventData): GtmTrackEvent => {
  return {
    eventData: {
      category,
      property,
      action,
      label,
      value,
    },
    event: 'trackEvent',
  };
};

export enum Category {
  Availability = 'datetime_selection',
  Reschedule = 'datetime_reschedule',
}

const category = isAvailabiltyRoute()
  ? Category.Availability
  : Category.Reschedule;

export enum Action {
  Select = 'select',
  Open = 'open',
  EditTime = 'edit_time',
  Edit = 'edit',
  Unselect = 'unselect',
  Null = '',
  Click = 'click',
  Load = 'load',
}

export const trackOpenEmployeeList = (serviceId?: string) =>
  track(
    createEvent({
      category,
      action: Action.Open,
      property: 'employee_list',
      label: serviceId,
    }),
  );

export const trackSelectEmployee = (employeeName: string, serviceId?: string) =>
  track(
    createEvent({
      category,
      action: Action.Select,
      property: 'employee_list',
      label: serviceId,
      value: employeeName,
    }),
  );

export const trackSelectDateTime = ({
  date,
  time,
  discount,
}: {
  date?: string;
  time?: string;
  discount?: string;
}) => {
  dayjs.extend(utc);

  const datetime = dayjs.utc(`${date}T${time}`).format();

  return track(
    createEvent({
      category,
      action: Action.EditTime,
      property: 'calendar',
      label: time ? datetime : date,
      value: discount === '0' ? undefined : discount,
    }),
  );
};
export const trackCalendarArrow = (selectedDate: string) =>
  track(
    createEvent({
      category,
      action: Action.Edit,
      property: 'calendar',
      label: selectedDate,
    }),
  );

export const trackRemoveService = (serviceId: string) =>
  track(
    createEvent({
      category,
      action: Action.Unselect,
      property: 'treatment',
      label: serviceId,
    }),
  );

export const trackAddService = () =>
  track(
    createEvent({
      category,
      action: Action.Click,
      property: 'add_treatment',
    }),
  );

export const trackNoAvailability = (date: string) =>
  track(
    createEvent({
      category,
      action: Action.Null,
      property: 'calendar',
      label: 'no_availability',
      value: transformDate(date),
    }),
  );

export const trackNoAvailabilityWithButton = (date: string) =>
  track(
    createEvent({
      category,
      action: Action.Null,
      property: 'calendar',
      label: 'no_availability_button',
      value: transformDate(date),
    }),
  );

export const trackClickNextAvailabilityButton = (date: string) =>
  track(
    createEvent({
      category,
      action: Action.Click,
      property: 'calendar',
      label: 'no_availability_button',
      value: transformDate(date),
    }),
  );

export const trackCheckoutButton = () =>
  track(
    createEvent({
      category,
      action: Action.Click,
      property: 'checkout_button',
    }),
  );

export const trackMultiStaffExperimentEnabled = (
  isMultiStaffSelectionEnabled?: boolean,
) => {
  const label = isMultiStaffSelectionEnabled
    ? 'multistaff_selection_experiment_enabled'
    : 'multistaff_selection_experiment_disabled';
  track(
    createEvent({
      category,
      action: Action.Load,
      label,
      property: 'page_load',
    }),
  );
};
